import { Language, Service } from '@yleisradio/areena-types';
import { SearchPage } from 'components/SearchPage';
import { LocationContextProvider } from 'contexts/LocationContext';
import apm from 'elastic-apm-node';
import { useTranslation } from 'hooks/useTranslation';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { FunctionComponent } from 'react';
import { searchRoutes } from 'utils/routes';
import {
  activeServiceQueryParameterName,
  searchQueryParameterName,
} from 'utils/search-params';
import { prettyPath } from 'utils/url';

type PageProps = Record<string, never>;

// This same component is used on parallel (Swedish) page "sok.tsx"
const Page: FunctionComponent<PageProps> = () => {
  const router = useRouter();
  const t = useTranslation();

  const activeService: Service =
    router.query[activeServiceQueryParameterName] === 'radio' ? 'radio' : 'tv';
  const searchQuery =
    typeof router.query[searchQueryParameterName] === 'string'
      ? router.query[searchQueryParameterName]
      : '';

  const setActiveService = (service: Service) => {
    const query = {
      [searchQueryParameterName]: searchQuery,
      [activeServiceQueryParameterName]: service,
    };

    void router.replace(
      {
        pathname: router.pathname,
        query,
      },
      prettyPath(router.asPath, query),
      { scroll: false, shallow: true }
    );
  };

  const setQuery = (newQuery: string) => {
    const query = {
      [searchQueryParameterName]: newQuery,
      [activeServiceQueryParameterName]: activeService,
    };

    void router.replace(
      {
        pathname: router.pathname,
        query,
      },
      prettyPath(router.asPath, query),
      { scroll: false, shallow: true }
    );
  };

  return (
    <>
      <Head>
        <title>{`${t('search')} | ${t('areena')}`}</title>
      </Head>
      <LocationContextProvider>
        <SearchPage
          activeService={activeService}
          searchQuery={searchQuery}
          setActiveService={setActiveService}
          setQuery={setQuery}
        />
      </LocationContextProvider>
    </>
  );
};

export default Page;

// This same function is used on parallel (Swedish) page "sok.tsx"
export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  locale,
  query,
  resolvedUrl,
}) => {
  const { pathname: path } = new URL(resolvedUrl, 'https://localhost/');

  apm.setTransactionName(`GET ${path}`);

  const language: Language = locale === 'sv' ? 'sv' : 'fi';
  const activeService: Service =
    query[activeServiceQueryParameterName] === 'radio' ? 'radio' : 'tv';
  const searchQuery =
    typeof query[searchQueryParameterName] === 'string'
      ? query[searchQueryParameterName]
      : '';

  if (path !== `/${searchRoutes[language]}`) {
    const redirectTo = `/${searchRoutes[language]}${
      searchQuery
        ? `?${searchQueryParameterName}=${encodeURIComponent(searchQuery)}&${activeServiceQueryParameterName}=${activeService}`
        : `?${activeServiceQueryParameterName}=${activeService}`
    }`;

    return { redirect: { destination: redirectTo, permanent: true } };
  }

  return {
    props: {},
  };
};
